"use client";

import { useState, useEffect, useLayoutEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import AuthModal from "@/app/components/AuthModal";
import { createClient } from "@/lib/supabase/client";
import {
  BASTION_ACCENT,
  BASTION_ACCENT_BORDER,
  BASTION_ACCENT_INK,
} from "@/lib/bastion-accent";
import { PRODUCT_OVERVIEW_PATH } from "@/lib/hub/public-nav-links";


/** @supabase/ssr keeps the browser session in `sb-<project>-auth-token` cookies (chunked as `.0`, `.1`). */
function hasSessionCookie(): boolean {
  return document.cookie.split(";").some((part) => /^\s*sb-[^=]+-auth-token(\.\d+)?=/.test(part));
}

type LandingNavItem = {
  label: string;
  href: string;
};

/** Standalone marketing pages only; landing sections are reached by scrolling, not from the nav. */
const DEFAULT_NAV_ITEMS: LandingNavItem[] = [
  { label: "Methodology", href: "/methodology" },
  { label: "Plans", href: "/pricing" },
  { label: "About", href: "/about" },
];

type LandingNav2Props = {
  items?: LandingNavItem[];
  /** Overrides the CTA text; by default guests see "Create free account", signed-in users "Open terminal". */
  ctaLabel?: string;
};

export default function LandingNav2({
  items = DEFAULT_NAV_ITEMS,
  ctaLabel,
}: LandingNav2Props) {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("register");
  // "unknown" until the client knows; the right-hand buttons stay hidden so guests' buttons never flash for members.
  const [authState, setAuthState] = useState<"unknown" | "guest" | "authed">("unknown");
  const isAuthed = authState === "authed";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Before first paint, guess from the Supabase session cookie; the auth check below confirms it. */
  useLayoutEffect(() => {
    setAuthState(hasSessionCookie() ? "authed" : "guest");
  }, []);

  /* Track auth so the CTA can send already-registered users straight to the Hub. */
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setAuthState(user ? "authed" : "guest");
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => setAuthState(session?.user ? "authed" : "guest"),
    );
    return () => subscription.unsubscribe();
  }, []);

  /* Highlight a nav item only after its section has crossed the top of the page. */
  useEffect(() => {
    const sectionIds = items
      .map((item) => (item.href.includes("#") ? item.href.split("#")[1] : ""))
      .filter(Boolean);

    const updateActive = () => {
      const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => Boolean(el));
      if (sections.length === 0) return;

      const marker = window.scrollY + 96;
      const firstTop = sections[0].offsetTop;

      if (marker < firstTop) {
        setActiveSection("");
        return;
      }

      let current = sections[0];
      for (const section of sections) {
        if (section.offsetTop <= marker) current = section;
        else break;
      }
      setActiveSection(`#${current.id}`);
    };

    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
    };
  }, [items]);

  const handleCTA = () => {
    // Already registered → skip the modal and go straight to the Hub.
    if (isAuthed) {
      window.location.href = "/hub";
      return;
    }
    setAuthMode("register");
    setAuthModalOpen(true);
  };

  const openLogin = () => {
    setAuthMode("login");
    setAuthModalOpen(true);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const hash = href.includes("#") ? `#${href.split("#")[1]}` : "";
    const onLanding =
      pathname === "/" ||
      pathname === "/landing" ||
      pathname === PRODUCT_OVERVIEW_PATH;
    if (hash && onLanding) {
      e.preventDefault();
      setMobileOpen(false);
      const target = document.querySelector(hash);
      if (target instanceof HTMLElement) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const offset = window.innerWidth < 768 ? 76 : 88;
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: "smooth" });
          });
        });
      }
      return;
    }
    // From /about (and other marketing pages) hash links must leave the page.
    // A signed-in visit to `/` opens the Hub, so those jumps go to /product.
    if (hash && !onLanding) {
      e.preventDefault();
      setMobileOpen(false);
      const dest = isAuthed
        ? `${PRODUCT_OVERVIEW_PATH}${hash}`
        : href.startsWith("/")
          ? href
          : `/${href}`;
      window.location.assign(dest);
      return;
    }
    setMobileOpen(false);
  };

  const renderLink = (item: LandingNavItem, variant: "desktop" | "mobile") => {
    const itemHash = item.href.includes("#") ? `#${item.href.split("#")[1]}` : "";
    const isActive = (itemHash && activeSection === itemHash) || (!itemHash && pathname === item.href);
    const className =
      variant === "desktop"
        ? `px-3 py-2 text-[14px] font-normal transition-colors duration-200 ${
            isActive ? "text-white" : "text-white/75 hover:text-white"
          }`
        : `block px-1 py-3 text-[15px] font-medium border-b border-white/[0.06] transition-colors ${
            isActive ? "text-white" : "text-white/70 hover:text-white"
          }`;
    const props = {
      className,
      "aria-current": isActive ? ("page" as const) : undefined,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavClick(e, item.href),
    };
    return itemHash ? (
      <a key={item.label} href={item.href} {...props}>
        {item.label}
      </a>
    ) : (
      <Link key={item.label} href={item.href} prefetch scroll {...props}>
        {item.label}
      </Link>
    );
  };

  const cta = ctaLabel ?? (isAuthed ? "Open terminal" : "Create free account");

  const ctaStyle = {
    background: BASTION_ACCENT,
    border: `1px solid ${BASTION_ACCENT_BORDER}`,
    color: BASTION_ACCENT_INK,
  };

  return (
    // Full-width bar pinned to the top: logo, then site sections, sign-in and CTA on the right.
    // Solid page colour so content never shows through; a hairline appears once the page scrolls.
    <header
      className="fixed top-0 left-0 right-0 z-50 select-none transition-[border-color] duration-300"
      style={{
        background: "#0e0e0e",
        borderBottom: `1px solid rgba(255, 255, 255, ${scrolled || mobileOpen ? 0.08 : 0})`,
      }}
    >
      <nav className="flex h-[72px] items-center px-6 md:px-10">
        <Link
          href={isAuthed || pathname === PRODUCT_OVERVIEW_PATH ? PRODUCT_OVERVIEW_PATH : "/"}
          className="flex items-center shrink-0 gap-2"
        >
          <Image src="/iconb-mark.png" alt="" width={21} height={21} />
          <span className="text-[16px] font-semibold tracking-tight text-white">Investment Bastion</span>
        </Link>

        <div className="hidden md:flex items-center ml-6">{items.map((item) => renderLink(item, "desktop"))}</div>

        <div
          className="ml-auto flex items-center gap-2 transition-opacity duration-200"
          style={{ opacity: authState === "unknown" ? 0 : 1 }}
        >
          {!isAuthed ? (
            <button
              onClick={openLogin}
              className="hidden md:inline-flex items-center h-9 px-4 rounded-lg text-[14px] font-medium text-white/85 border border-white/15 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              Log in
            </button>
          ) : null}
          {/* Signed-in CTA is a real link so it can be opened in a new tab. */}
          {isAuthed ? (
            <a
              href="/hub"
              className="hidden md:inline-flex items-center h-9 px-4 rounded-lg text-[14px] font-semibold transition-all duration-200 hover:brightness-105"
              style={ctaStyle}
            >
              {cta}
            </a>
          ) : (
            <button
              onClick={handleCTA}
              className="hidden md:inline-flex items-center h-9 px-4 rounded-lg text-[14px] font-semibold transition-all duration-200 hover:brightness-105"
              style={ctaStyle}
            >
              {cta}
            </button>
          )}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-all"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-white/[0.06]">
          <div className="px-6 pb-5">
            {items.map((item) => renderLink(item, "mobile"))}
            {!isAuthed ? (
              <button
                onClick={() => {
                  setMobileOpen(false);
                  openLogin();
                }}
                className="mt-4 w-full flex items-center justify-center h-10 rounded-lg text-sm font-medium text-white/85 border border-white/15"
              >
                Log in
              </button>
            ) : null}
            {isAuthed ? (
              <a
                href="/hub"
                onClick={() => setMobileOpen(false)}
                className="mt-2 w-full flex items-center justify-center h-10 rounded-lg text-sm font-semibold"
                style={ctaStyle}
              >
                {cta}
              </a>
            ) : (
              <button
                onClick={() => {
                  setMobileOpen(false);
                  handleCTA();
                }}
                className="mt-2 w-full flex items-center justify-center h-10 rounded-lg text-sm font-semibold"
                style={ctaStyle}
              >
                {cta}
              </button>
            )}
          </div>
        </div>
      )}

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
        onAuthSuccess={() => {
          setAuthModalOpen(false);
          window.location.href = "/hub";
        }}
      />
    </header>
  );
}
